import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Users, ArrowLeftRight, Gift, TrendingUp, DollarSign, UserCheck, QrCode, Copy, ExternalLink } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { API } from "@/lib/api";
import { brand } from "@/lib/brand";


function PortalCard({ vendorId, authHeaders }) {
  const [copied, setCopied] = React.useState(false);
  const [slug, setSlug] = React.useState(null);

  React.useEffect(() => {
    if (!vendorId) return;
    axios.get(`${API}/vendors/${vendorId}`, { headers: authHeaders() })
      .then(({ data }) => setSlug(data.slug || vendorId))
      .catch(() => setSlug(vendorId));
  }, [vendorId]);

  const portalUrl = slug ? `${window.location.origin}/${slug}` : `${window.location.origin}/portal?client=${vendorId}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=160x160&margin=8&data=${encodeURIComponent(portalUrl)}`;
  const handleCopy = () => { navigator.clipboard.writeText(portalUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  return (
    <Card className="bg-white border-0 shadow-sm rounded-2xl" data-testid="portal-card">
      <CardContent className="px-5 py-4 flex items-center gap-5">
        <img src={qrUrl} alt="Portal QR" className="w-20 h-20 rounded-xl border border-ink/10 shrink-0" data-testid="portal-qr-code" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <QrCode size={14} className="text-ink/50" />
            <p className="text-sm font-semibold text-ink">Customer Portal</p>
          </div>
          <p className="text-xs text-ink/40 mb-2">Share this link or QR code with your customers to check their points, tier and rewards.</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 px-3 py-1.5 bg-surface border border-ink/10 rounded-lg text-xs font-mono text-ink/60 truncate">{portalUrl}</code>
            <button onClick={handleCopy} className="px-3 py-1.5 rounded-full border border-ink/15 text-xs font-medium text-ink/60 hover:text-ink hover:bg-purple-50 flex items-center gap-1.5 shrink-0" data-testid="copy-portal-url">
              <Copy size={12} /> {copied ? "Copied!" : "Copy"}
            </button>
            <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 rounded-full border border-ink/15 text-xs font-medium text-ink/60 hover:text-ink hover:bg-purple-50 flex items-center gap-1.5 shrink-0" data-testid="open-portal-link">
              <ExternalLink size={12} /> Open
            </a>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { authHeaders, isAdmin, isVendor, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [topVendors, setTopVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const h = authHeaders();
    const calls = [
      axios.get(`${API}/analytics/stats`, { headers: h }),
      axios.get(`${API}/analytics/chart-data`, { headers: h }),
    ];
    if (isAdmin) calls.push(axios.get(`${API}/analytics/top-vendors`, { headers: h }));
    Promise.all(calls).then(([s, c, t]) => {
      setStats(s.data);
      setChartData(c.data || []);
      if (t) setTopVendors(t.data || []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-ink/30">Loading...</div>;
  if (!stats) return null;

  const statCards = isAdmin ? [
    { label: "Total Vendors", value: stats.total_vendors, icon: Building2, color: "bg-purple-100 text-ink" },
    { label: "Total Customers", value: stats.total_customers?.toLocaleString(), icon: Users, color: "bg-purple-100 text-ink" },
    { label: "Transactions", value: stats.total_transactions?.toLocaleString(), icon: ArrowLeftRight, color: "bg-purple-100 text-ink" },
    { label: "Points Issued", value: stats.total_points_issued?.toLocaleString(), icon: TrendingUp, color: "bg-purple-100 text-ink" },
    { label: "Rewards Active", value: stats.rewards_issued, icon: Gift, color: "bg-purple-100 text-ink" },
    { label: "Revenue", value: `£${stats.total_revenue_gbp?.toLocaleString()}`, icon: DollarSign, color: "bg-purple-100 text-ink" },
  ] : [
    { label: "My Customers", value: stats.total_customers?.toLocaleString(), icon: Users, color: "bg-purple-100 text-ink" },
    { label: "Transactions", value: stats.total_transactions?.toLocaleString(), icon: ArrowLeftRight, color: "bg-purple-100 text-ink" },
    { label: "Points Issued", value: stats.total_points_issued?.toLocaleString(), icon: TrendingUp, color: "bg-purple-100 text-ink" },
    { label: "Rewards Active", value: stats.rewards_issued, icon: Gift, color: "bg-purple-100 text-ink" },
    { label: "Redeemed", value: stats.rewards_redeemed, icon: UserCheck, color: "bg-purple-100 text-ink" },
    { label: "Revenue", value: `£${stats.total_revenue_gbp?.toLocaleString()}`, icon: DollarSign, color: "bg-purple-100 text-ink" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl font-bold text-ink tracking-tight">
          {isAdmin ? "Dashboard Overview" : `Welcome, ${user?.name || "Vendor"}`}
        </h1>
        <p className="text-sm text-ink/40 mt-0.5">
          {isAdmin ? "Platform-wide loyalty metrics" : "Your loyalty program at a glance"}
        </p>
      </div>

      {isVendor && user?.vendor_id && <PortalCard vendorId={user.vendor_id} authHeaders={authHeaders} />}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="stat-cards">
        {statCards.map(({ label, value, icon: Icon, color }) => (
          <Card key={label} className="bg-white border-0 shadow-sm rounded-2xl">
            <CardContent className="px-4 py-4">
              <div className={`w-9 h-9 rounded-xl ${color} flex items-center justify-center mb-2.5`}>
                <Icon size={15} />
              </div>
              <p className="text-xl font-bold text-ink tracking-tight">{value}</p>
              <p className="text-xs text-ink/40 mt-0.5">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="bg-white border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-0 pt-5 px-5"><CardTitle className="text-sm font-semibold text-ink">Points Activity (30 days)</CardTitle></CardHeader>
          <CardContent className="px-2 pb-4">
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: brand.color.ink }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: brand.color.ink }} width={40} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} />
                <Line type="monotone" dataKey="points" stroke={brand.color.purple[400]} strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-0 pt-5 px-5"><CardTitle className="text-sm font-semibold text-ink">Daily Revenue (30 days)</CardTitle></CardHeader>
          <CardContent className="px-2 pb-4">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: brand.color.ink }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: brand.color.ink }} width={40} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} />
                <Bar dataKey="spend" fill={brand.color.purple[500]} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {isAdmin && topVendors.length > 0 && (
        <Card className="bg-white border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-2 pt-5 px-5"><CardTitle className="text-sm font-semibold text-ink">Top Vendors by Points</CardTitle></CardHeader>
          <CardContent className="px-5 pb-4">
            <div className="space-y-2.5">
              {topVendors.map((v, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <span className="w-6 h-6 rounded-lg bg-purple-50 flex items-center justify-center text-[10px] text-ink/50 font-bold">{i + 1}</span>
                  <span className="flex-1 font-medium text-ink">{v.vendor_name}</span>
                  <span className="font-semibold text-ink">{v.total_points?.toLocaleString()} pts</span>
                  <span className="text-ink/30 text-xs">{v.tx_count} txns</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
